import { Chakra } from "@airhelp/react-ui-components";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import getPageUrl from "utils/getPageUrl";
import AirHelpScoreWrapper from "../AirHelpScoreWrapper";
import type { AirportRankingProps } from "./AirportRanking.d";
import {
  CTA,
  Hero,
  TopThree,
  HowScoreIsCalculated,
  RelatedArticles,
  ScoreTag,
  Share,
  JoinTheCommunity,
  About,
} from "../common";
import TopThreeCard from "../common/TopThree/TopThreeCard";
import { HOW_SCORE_IS_CALCULATED_AIRPORTS_ITEMS, AIRPORTS_METHODOLOGY_PDF_LINK, AIRLINE_POST_IMAGE } from "./consts";
import AirportRankingTable from "./AirportRankingTable";
import columns from "./AirportRankingTable/config";

const { Box, Heading, Text } = Chakra;

const AirportRanking = ({
  story,
  cmsLanguagesData,
  serverSideStoryblokPages,
  airportsScores,
  relatedArticles,
}: AirportRankingProps) => {
  const translations = useTranslations();
  const language = useLanguage();
  const topThreeAirports = airportsScores.slice(0, 3);

  return (
    <AirHelpScoreWrapper
      story={story}
      cmsLanguagesData={cmsLanguagesData}
      serverSideStoryblokPages={serverSideStoryblokPages}
    >
      <Hero
        heading={translations("global_airport_ranking")}
        subHeading={translations("airhelp_score_2019")}
        text={translations("as_the_global_rights_expert")}
      />
      <Box mt={{ base: 12, lg: 20 }} bgGradient="linear(to-b, greyscale.100 50%, greyscale.300 0)">
        <CTA
          text={translations("how_do_i_know")}
          actionButtonText={translations("check_compensation")}
          actionButtonHref={getPageUrl(language, "webappNewClaim")}
          checkboxes={[translations("all_airlines"), translations("all_countries"), translations("no_win_no_fee")]}
        />
      </Box>
      <TopThree heading={translations("best_airports_pax_exp")}>
        {topThreeAirports.map((item, index) => {
          const {
            dep_airport_name: airportName,
            airport_score: score,
            dep_city_name: city,
            dep_airport_country_name: country,
          } = item;

          return (
            <TopThreeCard key={airportName}>
              <Heading variant="h3" as="h3" mb={2}>
                {airportName}
              </Heading>
              <Text color="greyscale.600" pb={6} justifyContent="none" mb="auto">
                {city}, {country}
              </Text>
              <ScoreTag index={index} score={score} size="lg" />
            </TopThreeCard>
          );
        })}
      </TopThree>
      <AirportRankingTable rows={airportsScores} columns={columns} heading="global_airport_ranking" />
      <HowScoreIsCalculated
        items={HOW_SCORE_IS_CALCULATED_AIRPORTS_ITEMS}
        learnMoreLink={AIRPORTS_METHODOLOGY_PDF_LINK}
      />
      <RelatedArticles relatedArticles={relatedArticles} fallbackFeaturedImage={AIRLINE_POST_IMAGE} />
      <About />
      <JoinTheCommunity />
      <Share />
    </AirHelpScoreWrapper>
  );
};

export default AirportRanking;
